import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { rem, stripUnit } from 'polished'

import fluidRange from '../../utils/fluidRange'

import getColor from '../../utils/getColor'
import { WidthContainer } from '../../utils/helpers.styles'

export const Header = styled.header`
  ${({
    theme,
    headerBackgroundColor,
    headerTextColor,
    navUnderLogo,
    navigationVisibility,
  }) => `
      display: none;
      background-color: ${getColor(theme, headerBackgroundColor)};
      position: relative;
      z-index: 2;
      padding-top: ${theme.spacing.xs};
      padding-bottom: ${navUnderLogo ? '0' : theme.spacing.xs};

      nav {
        a {
          display: inline-block;
          text-decoration: none;
          line-height: 1;
        }
      }

      .sylex-image {
        min-width: 130px;
      }

      .primary-link a,
      .primary-link > span {
        color: ${getColor(theme, headerTextColor)};
      }

      @media (min-width: 1200px) {
        display: block !important;
      }

      @media (min-width: ${theme.breakpoints.xl}) {
        .primary-link {
          display: ${
            navigationVisibility === 'mobile only' ? 'none !important' : 'flex'
          };}
      }
    `}
`

export const TopNav = styled.nav`
  ${({ theme, topBarTextColor, headerTextColor }) => `
  a {
    border-left: 1px solid ${getColor(
      theme,
      topBarTextColor || headerTextColor || 'dark'
    )};
    padding: 0 1em;
    color: ${getColor(theme, topBarTextColor)};

    &:first-of-type {
      padding-left: 0;
      border-left: none;
    }

    &:last-of-type {
      padding-right: 0;
      ${({ reverseTopBar }) => !reverseTopBar && 'padding-right: 0;'}
    }
  `}
`

export const TopBar = styled.header`
  ${({
    theme,
    reverseTopBar,
    headerBackgroundColor,
    headerTextColor,
    topBarBackgroundColor,
    topBarTextColor,
  }) => {
    const flexDirection = reverseTopBar ? 'row-reverse' : 'row'

    return css`
      ${WidthContainer({ theme })};
      background-color: ${getColor(
        theme,
        topBarBackgroundColor || headerBackgroundColor
      )};
      color: ${getColor(theme, topBarTextColor || headerTextColor)};
      font-size: ${rem(theme.fonts.body.xs.size)};
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: space-between;
      align-items: center;
      padding-top: ${theme.spacing.xxs};
      padding-bottom: ${theme.spacing.xxs};
      flex-direction: ${flexDirection};

      @media (max-width: ${theme.breakpoints.xl}) {
        padding-left: ${theme.spacing.sm};
        padding-right: ${theme.spacing.sm};
      }

      a {
        text-decoration: none;

        &:visited {
          color: inherit;
        }
      }
    `
  }}
`

export const TextBlock = styled.div`
  ${({
    theme,
    navUnderLogo,
    textBlockAlignment,
    headerTextColor,
    navigationTextColor,
  }) => {
    const textColor = getColor(
      theme,
      headerTextColor || (!navUnderLogo && navigationTextColor) || 'dark'
    )

    return css`
      display: flex;
      gap: 4px;
      flex-direction: column;
      text-align: ${textBlockAlignment || 'left'};
      margin-left: auto;

      * {
        color: ${textColor};
        margin-top: 0;
        margin-bottom: 0;
      }

      .countdown-timer {
        color: ${textColor};
      }

      .countdown-block {
        border-color: ${textColor};
      }
    `
  }}
`

export const TopBarText = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 8px;

    * {
      font-size: ${rem(theme.fonts.body.xs.size)} !important;
      margin-top: 0;
      margin-bottom: 0;
    }
  `}
`

export const CallToActions = styled.div(
  ({ theme, textBlock }) => css`
    display: flex;
    gap: ${theme.spacing.sm};
    margin-left: ${!textBlock ? 'auto' : '0'};
  `
)

export const BottomBar = styled.div`
  ${({
    theme,
    navUnderLogo,
    navigationBackgroundColor,
    headerBackgroundColor,
    navigationTextColor,
  }) => {
    const backgroundColor = getColor(
      theme,
      (!navUnderLogo && navigationBackgroundColor) || headerBackgroundColor
    )
    const logoMarginRight = navUnderLogo ? 'auto' : '0'
    const alignment = navUnderLogo ? 'flex-start' : 'space-between'
    const borderBottom =
      (navUnderLogo && navigationBackgroundColor === 'white') ||
      navigationBackgroundColor === 'light'
        ? '1px solid #E3E5E7'
        : '0'

    return css`
      ${WidthContainer({ theme })};
      background-color: ${backgroundColor};
      display: flex;
      align-items: center;
      justify-content: ${alignment};
      gap: ${theme.spacing.sm};
      border-bottom: ${borderBottom};
      color: ${getColor(theme, navigationTextColor)};
      padding-bottom: ${navUnderLogo ? theme.spacing.xs : '0'};

      > img {
        margin-right: ${logoMarginRight};
      }
    `
  }}
`

export const LocationChanger = styled.div`
  display: flex;
  align-items: center;
`

export const ChildContainer = styled.div`
  ${({ theme, headerTextColor, topBarTextColor }) => `
      margin-left: 6px;
      border-left: 1px solid ${getColor(
        theme,
        topBarTextColor || headerTextColor || 'dark'
      )};
      padding-left: 6px;
      cursor: pointer;
    `}
`

export const LocationInformation = styled.div`
  display: flex;
  align-items: center;

  > svg {
    width: 16px;
    height: 16px;
  }
`

export const MainNav = styled.nav`
  ${({
    theme,
    headerBackgroundColor,
    headerTextColor,
    navigationBackgroundColor,
    navigationTextColor,
    navUnderLogo,
    hoverBackgroundColor,
    hoverTextColor,
  }) => {
    const backgroundColor = getColor(
      theme,
      navigationBackgroundColor || headerBackgroundColor
    )
    const textColor = getColor(
      theme,
      navigationTextColor || headerTextColor || 'dark'
    )
    const hoverBg = getColor(theme, hoverBackgroundColor || 'light')
    const hoverText = getColor(theme, hoverTextColor || 'dark')
    const paddingLeft = navUnderLogo ? '22px' : '0'
    const paddingTop = navUnderLogo ? '12px' : '0'
    const marginRightSpan = theme.spacing.xs

    return css`
      background: ${backgroundColor};
      color: ${textColor};
      display: flex;
      align-items: center;
      align-self: flex-end;
      flex-basis: fit-content;
      padding-left: ${paddingLeft};
      padding-top: ${navUnderLogo ? '0px' : paddingTop};
      ${navUnderLogo && WidthContainer({ theme })};

      > span:first-of-type {
        margin-right: ${marginRightSpan};
      }

      span {
        cursor: pointer;
      }

      .sylex-image {
        min-height: 25px;
      }

      .primary-link {
        background: ${backgroundColor};
        color: ${textColor};
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.1s, background-color 0.1s;
        align-self: flex-end;
        white-space: nowrap;

        &:hover {
          background-color: ${hoverBg};

          > a,
          .leshen-link {
            color: ${hoverText};
          }
        }

        &:hover .subnav {
          display: flex;
        }

        > a,
        > span {
          height: 100%;
          display: flex;
          align-items: center;
          padding: ${`18px ${theme.spacing.xs}`};
          color: ${textColor};
        }

        &:first-of-type {
          margin-left: ${!navUnderLogo ? theme.spacing.sm : '0'};
        }
      }

      .subnav {
        background-color: ${theme.colors.white.base.hex};
        box-shadow: ${theme.shadow};
        border-bottom-right-radius: ${theme.radius};
        border-bottom-left-radius: ${theme.radius};
        display: none;
        position: absolute;
        top: ${navUnderLogo ? '100%' : 'calc(100% - 12px)'};
        flex-direction: column;
        list-style: none;

        li:last-child {
          border-bottom-right-radius: inherit;
          border-bottom-left-radius: inherit;
        }

        li:hover {
          background-color: ${theme.colors.light.base.hex};
          color: ${theme.colors.dark.base.hex};
        }

        a {
          color: ${theme.colors.dark.base.hex};
          padding: ${theme.spacing.sm};
          padding-right: ${theme.spacing.xl};
          display: block;
          cursor: pointer;
          transition: color 0.25s, background-color 0.25s;
          text-transform: initial;

          :last-child {
            border-bottom-right-radius: inherit;
            border-bottom-left-radius: inherit;
          }
        }
      }
    `
  }}
`

export const MegaMenu = styled.nav(
  ({ theme, featured }) => css`
    .subnav {
      color: ${theme.colors.dark.base.hex};
      box-shadow: ${theme.shadow};
      max-width: 95vw;
      left: 0;
      right: 0;
      margin: auto;

      & {
        flex-direction: row;
      }

      .subnav-container {
        padding: ${theme.spacing.lg};
        width: ${featured ? '75%' : '100%'};
      }

      ul {
        grid-gap: ${theme.spacing.lg};
        flex-direction: row;
        display: flex;
        justify-content: space-between;
        list-style: none;

        div,
        li {
          padding-right: ${theme.spacing.lg};
          width: 100%;
        }

        .mega-menu-subheading {
          margin-bottom: ${theme.spacing.xs};
        }

        .mega-menu-subheading,
        & > li a {
          font-weight: ${theme.fonts.subheadings.md.weight};
          border-bottom: 1px solid ${theme.colors.dark.base.hex};
          display: inline-block;

          &:hover {
            border-bottom: 1px solid ${theme.colors.primary.base.hex};
          }

          a {
            color: ${theme.colors.dark.base.hex};
            padding: ${theme.spacing.xxs};
            padding-left: 0;

            text-decoration: none;

            &:hover {
              background-color: initial;
              color: ${theme.colors.dark.base.hex};
            }
          }
        }

        li {
          a,
          span {
            color: ${theme.colors.dark.base.hex};
            padding: ${theme.spacing.xxs};
            padding-left: 0;

            &:hover {
              background-color: initial;
              color: ${theme.colors.dark.base.hex};
            }
          }
        }
      }
    }
  `
)

export const FeaturedContent = styled.div`
  background-color: ${({ theme }) => theme.colors.light.base.hex};
  padding: ${({ theme }) => theme.spacing.lg};
  width: 25%;
`

export const CallButton = styled.div(
  ({ theme, dark }) => css`
    color: ${dark
      ? theme.colors.light.lighter.hex
      : theme.colors.primary.base.hex};
    display: flex;
    text-decoration: none;

    > svg {
      margin-right: ${theme.spacing.min};
    }
  `
)

export const MobileMenuButton = styled.div(
  ({ theme, headerTextColor }) => css`
    svg {
      margin: inherit;

      path {
        fill: ${getColor(theme, headerTextColor || 'dark')};
      }
    }
  `
)

export const MobileHeader = styled.header(
  ({
    theme,
    navigationTextColor,
    headerBackgroundColor,
    headerTextColor,
    navigationVisibility,
  }) => css`
    background-color: ${getColor(theme, headerBackgroundColor)};
    color: ${getColor(theme, navigationTextColor)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};

    .menu-button {
      display: ${navigationVisibility === 'desktop only' ? 'none' : 'flex'};
      svg,
      path {
        fill: ${headerTextColor && getColor(theme, headerTextColor)};
      }
    }

    @media (min-width: ${theme.breakpoints.xl}) {
      display: none !important;
    }
  `
)

export const MobileNav = styled.nav(
  ({
    theme,
    navigationBackgroundColor,
    navigationTextColor,
    headerBackgroundColor,
    headerTextColor,
  }) => css`
    position: absolute;
    display: flex;
    flex-direction: column;
    width: 100%;
    z-index: 25;
    background-color: ${getColor(
      theme,
      navigationBackgroundColor || headerTextColor
    )};

    a {
      color: ${getColor(theme, navigationTextColor || headerTextColor)};
      padding: ${theme.spacing.xs} ${theme.spacing.md};
      display: flex;
      width: 100%;
      color: inherit;
      text-decoration: none;
    }

    button,
    a {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: transparent;
      border: none;
    }

    .back-button {
      background-color: ${getColor(
        theme,
        navigationBackgroundColor || headerBackgroundColor || 'white'
      )};
      color: ${getColor(
        theme,
        navigationTextColor || headerTextColor || 'dark'
      )};
      padding: ${theme.spacing.xs} ${theme.spacing.md};
      justify-content: flex-start;
      width: 100%;
      text-align: left;

      svg {
        margin: 0;
        path {
          fill: ${getColor(
            theme,
            navigationTextColor || headerTextColor || 'dark'
          )};
        }
      }
    }

    ul {
      background-color: ${getColor(theme, navigationBackgroundColor)};
      color: ${getColor(theme, navigationTextColor)};

      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      list-style-type: none;
      /* 55px is the height of the header */
      height: calc(100vh - 55px);

      li {
        background-color: ${getColor(
          theme,
          navigationBackgroundColor || headerBackgroundColor || 'white'
        )};
        color: ${getColor(theme, navigationTextColor || headerTextColor)};
        border-bottom: 1px solid
          ${getColor(theme, navigationTextColor || headerTextColor)};
        display: flex;
        width: 100%;

        button {
          padding: ${theme.spacing.xs} ${theme.spacing.md};
          color: ${getColor(theme, navigationTextColor)};
          width: 100%;
          text-align: left;
          text-decoration: none;
          transition: background-color 0.3s;

          &:active {
            background-color: ${getColor(theme, navigationBackgroundColor)};
            color: ${getColor(theme, navigationTextColor)};
          }

          svg {
            margin: 0;
            path {
              fill: ${theme.colors[navigationTextColor]?.base.hex || 'inherit'};
            }
          }
        }
      }
    }
  `
)

export const Border = styled.header(
  ({ borderColor, borderHeight }) => css`
    background: ${borderColor};
    height: ${`${borderHeight}px`};
  `
)

export const MegaMenuTitle = styled.span(
  ({ theme }) => css`
    ${fluidRange('font-size', stripUnit(theme.fonts.subheadings.md.size))}
    display: block;
    color: ${theme.colors.dark.base.hex};
    font-weight: ${theme.fonts.subheadings.md.weight};
    margin-bottom: 12px;
  `
)
